import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, notifyError } from "../lib/api";
import { placeTireFromHistory } from "../lib/tireHistory";
import { tyreKindCount } from "../lib/tyres";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import Combobox from "../components/Combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Plus,
  Search,
  PackageOpen,
  Loader2,
  ArrowUpRight,
  MapPin,
  History,
  Trash2,
} from "lucide-react";

const emptyTire = () => ({
  brand: "",
  size: "",
  dot: "",
  tread_depth_mm: "",
  quantity: 4,
  season: "kis",
});

const emptyForm = {
  customer_id: "",
  vehicle_id: "",
  warehouse_id: "",
  location: "",
  fee: 0,
  notes: "",
  expected_pickup_date: "",
};

function seasonLabel(s) {
  return s === "yaz" ? "Yaz" : s === "kis" ? "Kış" : "4 Mevsim";
}

export default function Storages() {
  const [params] = useSearchParams();
  const { defaultWarehouseId } = useAuth();
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("depoda");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyForm, warehouse_id: defaultWarehouseId || "" });
  const [tires, setTires] = useState([emptyTire()]);
  const [saving, setSaving] = useState(false);
  const [freeLocs, setFreeLocs] = useState([]);
  const [freeLocsOpen, setFreeLocsOpen] = useState(false);
  const [freeLocsLoading, setFreeLocsLoading] = useState(false);
  const [history, setHistory] = useState({ open: false, loading: false, items: [] });

  const showFreeLocations = async () => {
    if (!form.warehouse_id) {
      toast.error("Önce depo seçin");
      return;
    }
    if (freeLocsOpen) {
      setFreeLocsOpen(false);
      return;
    }
    setFreeLocsLoading(true);
    try {
      const { data } = await api.get(`/warehouses/${form.warehouse_id}/free-locations`);
      setFreeLocs(data.locations || []);
      setFreeLocsOpen(true);
    } catch (e) {
      notifyError(e);
    } finally {
      setFreeLocsLoading(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tab !== "all") params.set("status", tab);
      if (q) params.set("q", q);
      const [s, c, w] = await Promise.all([
        api.get(`/storages?${params.toString()}`),
        customers.length ? Promise.resolve({ data: customers }) : api.get("/customers"),
        warehouses.length ? Promise.resolve({ data: warehouses }) : api.get("/warehouses"),
      ]);
      setList(s.data);
      if (!customers.length) setCustomers(c.data);
      if (!warehouses.length) setWarehouses(w.data);
    } catch (e) {
      notifyError(e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [tab, q]);

  useEffect(() => { load(); }, [load]);

  // Varsayılan depoyu belirle: kullanıcı profilindeki varsayılan → is_default
  // işaretli depo → ilk depo.
  const resolveDefaultWarehouse = useCallback(() => {
    if (defaultWarehouseId) return defaultWarehouseId;
    const def = warehouses.find((w) => w.is_default);
    return def?.id || warehouses[0]?.id || "";
  }, [defaultWarehouseId, warehouses]);

  useEffect(() => {
    const cid = params.get("customer");
    const vid = params.get("vehicle");
    if (cid) {
      setForm({ ...emptyForm, customer_id: cid, vehicle_id: vid || "", warehouse_id: defaultWarehouseId || "" });
      setTires([emptyTire()]);
      setOpen(true);
    }
    // eslint-disable-next-line
  }, []);

  // Depolar yüklendiğinde ve modal açıkken depo seçili değilse varsayılanı ata.
  useEffect(() => {
    if (open && !form.warehouse_id) {
      const wid = resolveDefaultWarehouse();
      if (wid) setForm((f) => ({ ...f, warehouse_id: wid }));
    }
    // eslint-disable-next-line
  }, [open, warehouses]);

  const openNewDialog = () => {
    setForm({ ...emptyForm, warehouse_id: resolveDefaultWarehouse() });
    setTires([emptyTire()]);
    setFreeLocsOpen(false);
    setHistory({ open: false, loading: false, items: [] });
    setOpen(true);
  };

  const selectedCustomer = customers.find((c) => c.id === form.customer_id);
  const vehicles = selectedCustomer?.vehicles || [];

  const customerItems = customers.map((c) => ({
    value: c.id,
    label: `${c.full_name} — ${c.phone}`,
    keywords: [
      c.full_name,
      c.phone,
      ...(c.vehicles || []).map((v) => v.plate),
    ].filter(Boolean),
  }));

  const vehicleItems = vehicles.map((v) => ({
    value: v.id,
    label: `${v.plate || "—"}${v.brand ? ` • ${v.brand}` : ""}${v.model ? ` ${v.model}` : ""}`,
    keywords: [v.plate, v.brand, v.model].filter(Boolean),
  }));

  const setTire = (idx, patch) =>
    setTires((prev) => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  const addTire = () => setTires((prev) => [...prev, emptyTire()]);
  const removeTire = (idx) =>
    setTires((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  // Yerleştirme kuralı ve kök neden açıklaması: lib/tireHistory.js
  const addTireFromHistory = (s) => {
    setTires((prev) => placeTireFromHistory(prev, s));
    toast.success("Lastik geçmişten eklendi");
  };

  const showVehicleHistory = async () => {
    if (!form.customer_id) {
      toast.error("Önce müşteri seçin");
      return;
    }
    if (history.open) {
      setHistory((h) => ({ ...h, open: false }));
      return;
    }
    setHistory({ open: true, loading: true, items: [] });
    try {
      const { data } = await api.get(`/storages?customer_id=${form.customer_id}`);
      const items = data.filter((s) =>
        form.vehicle_id ? s.vehicle_id === form.vehicle_id : true
      );
      setHistory({ open: true, loading: false, items });
    } catch (e) {
      notifyError(e);
      setHistory({ open: false, loading: false, items: [] });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) {
      toast.error("Müşteri seçin");
      return;
    }
    const invalid = tires.some((t) => !t.brand.trim() || !t.size.trim());
    if (invalid) {
      toast.error("Her lastik için Marka ve Ebat zorunludur");
      return;
    }
    setSaving(true);
    try {
      const pickup = form.expected_pickup_date
        ? new Date(form.expected_pickup_date + "T12:00:00").toISOString()
        : null;
      // Tüm lastik çeşitleri TEK bir emanet kaydında (tek makbuz) toplanır;
      // ücret kayıt seviyesinde tek olarak girilir.
      const items = tires.map((t) => ({
        brand: t.brand.trim(),
        size: t.size.trim(),
        dot: t.dot || null,
        season: t.season,
        quantity: Number(t.quantity) || 1,
        tread_depth_mm: t.tread_depth_mm ? Number(t.tread_depth_mm) : null,
      }));
      await api.post("/storages", {
        customer_id: form.customer_id,
        vehicle_id: form.vehicle_id || null,
        warehouse_id: form.warehouse_id || null,
        location: form.location,
        notes: form.notes,
        expected_pickup_date: pickup,
        fee: Number(form.fee) || 0,
        items,
      });
      toast.success("Emanet kaydedildi");
      setOpen(false);
      setForm({ ...emptyForm, warehouse_id: resolveDefaultWarehouse() });
      setTires([emptyTire()]);
      await load();
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto space-y-6" data-testid="storages-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow text-amber-600">Depo</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">
            Emanetler
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Lastik emanetlerini yönetin, QR kod oluşturun ve deponuza alın.
          </p>
        </div>
        <Button
          onClick={openNewDialog}
          className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
          data-testid="add-storage-btn"
        >
          <Plus className="h-4 w-4 mr-2" /> Yeni Emanet
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Tabs value={tab} onValueChange={setTab} className="shrink-0">
          <TabsList className="rounded-sm bg-muted">
            <TabsTrigger value="depoda" className="rounded-sm" data-testid="tab-depoda">Depoda</TabsTrigger>
            <TabsTrigger value="teslim_edildi" className="rounded-sm" data-testid="tab-teslim">Teslim Edilen</TabsTrigger>
            <TabsTrigger value="all" className="rounded-sm" data-testid="tab-all">Tümü</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Müşteri, plaka, marka, ebat veya raf ara..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-10 h-10 rounded-sm bg-card"
            data-testid="storage-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
      ) : list.length === 0 ? (
        <Card className="p-12 text-center rounded-sm">
          <PackageOpen className="h-10 w-10 mx-auto text-muted-foreground" />
          <div className="font-display font-bold text-lg mt-3">Emanet yok</div>
          <p className="text-sm text-muted-foreground mt-2">İlk emanet kaydını oluşturun.</p>
        </Card>
      ) : (
        <Card className="rounded-sm border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-foreground text-background">
                <tr>
                  <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Müşteri</th>
                  <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Lastik</th>
                  <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Adet</th>
                  <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Sezon</th>
                  <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Depo / Raf</th>
                  <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Durum</th>
                  <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Tarih</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {list.map((s) => (
                  <tr key={s.id} className="hover:bg-muted/40 transition-colors" data-testid={`storage-row-${s.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{s.customer_name || "—"}</div>
                      <div className="text-xs text-muted-foreground font-mono">{s.plate || s.customer_phone || ""}</div>
                    </td>
                    <td className="px-4 py-3">
                      {/* Tek çeşitte mevcut marka/ölçü gösterimi korunur;
                          birden fazlaysa çeşit sayısı yazılır. */}
                      {tyreKindCount(s) > 1 ? (
                        <div className="font-medium">{tyreKindCount(s)} Çeşit Lastik</div>
                      ) : (
                        <>
                          <div className="font-medium">{s.brand}</div>
                          <div className="text-xs text-muted-foreground font-mono">{s.size}</div>
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono">{s.quantity}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className="rounded-sm uppercase tracking-wider text-xs">
                        {s.season === "yaz" ? "Yaz" : s.season === "kis" ? "Kış" : "4 Mev"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-xs">{s.warehouse_name || "—"}</div>
                      <div className="text-xs text-muted-foreground font-mono">{s.location || "—"}</div>
                    </td>
                    <td className="px-4 py-3">
                      {s.status === "depoda" ? (
                        <div className="flex flex-col items-start gap-0.5">
                          <Badge className="rounded-sm bg-amber-500 text-white hover:bg-amber-500">Depoda</Badge>
                          {s.warehouse_name && (
                            <span className="text-[10px] text-muted-foreground font-mono uppercase tracking-wider">
                              {s.warehouse_name}
                            </span>
                          )}
                        </div>
                      ) : (
                        <Badge variant="secondary" className="rounded-sm">Teslim Edilen</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-muted-foreground whitespace-nowrap">
                      {new Date(s.check_in_date).toLocaleDateString("tr-TR")}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/emanetler/${s.id}`}
                        className="inline-flex items-center gap-1 text-foreground hover:text-amber-600 font-semibold"
                        data-testid={`view-storage-${s.id}`}
                      >
                        Aç <ArrowUpRight className="h-3.5 w-3.5" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Yeni Emanet Kaydı</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="storage-form">
            {/* Müşteri — aranabilir (ad, telefon, plaka) */}
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Müşteri *</Label>
              <Combobox
                value={form.customer_id}
                onChange={(v) => {
                  setForm({ ...form, customer_id: v, vehicle_id: "" });
                  setHistory({ open: false, loading: false, items: [] });
                }}
                items={customerItems}
                placeholder="Müşteri seçin"
                searchPlaceholder="Ad, telefon veya plaka ara..."
                emptyText="Müşteri bulunamadı"
                testId="storage-customer-select"
              />
              {customers.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Önce müşteri eklemeniz gerekiyor. <Link to="/musteriler" className="underline">Müşteriler</Link>
                </p>
              )}
            </div>

            {/* Araç — aranabilir (plaka) */}
            {vehicles.length >= 1 && (
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Araç</Label>
                <Combobox
                  value={form.vehicle_id || ""}
                  onChange={(v) => {
                    setForm({ ...form, vehicle_id: v });
                    setHistory({ open: false, loading: false, items: [] });
                  }}
                  items={vehicleItems}
                  placeholder="Araç seçin"
                  searchPlaceholder="Plaka ara..."
                  emptyText="Araç bulunamadı"
                  testId="storage-vehicle-select"
                />
              </div>
            )}

            {/* Araç geçmişi — seçilen araca ait geçmiş emanetler */}
            {form.vehicle_id && (
              <div className="space-y-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={showVehicleHistory}
                  disabled={history.loading}
                  className="rounded-sm"
                  data-testid="vehicle-history-btn"
                >
                  {history.loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <History className="h-4 w-4 mr-2" />
                  )}
                  {history.open ? "Geçmişi Gizle" : "Geçmişi Göster"}
                </Button>
                {history.open && !history.loading && (
                  <div className="rounded-sm border border-border bg-muted/40 p-2" data-testid="vehicle-history-list">
                    {history.items.length === 0 ? (
                      <p className="text-xs text-muted-foreground px-1 py-2">Bu araç için geçmiş kayıt bulunamadı.</p>
                    ) : (
                      <div className="divide-y divide-border">
                        {history.items.map((s) => (
                          <div key={s.id} className="flex items-center justify-between gap-3 py-2 px-1" data-testid={`history-item-${s.id}`}>
                            <div className="min-w-0">
                              <div className="text-sm font-medium truncate">
                                {s.brand} <span className="font-mono text-muted-foreground">{s.size}</span>
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {s.quantity} adet • {seasonLabel(s.season)}
                                {s.dot ? ` • DOT ${s.dot}` : ""} • {new Date(s.check_in_date).toLocaleDateString("tr-TR")}
                                {s.status === "depoda" ? " • Depoda" : " • Teslim Edilen"}
                              </div>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => addTireFromHistory(s)}
                              className="rounded-sm shrink-0"
                              data-testid={`history-add-${s.id}`}
                            >
                              <Plus className="h-3.5 w-3.5 mr-1" /> Ekle
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Ortak: depo / raf */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Depo</Label>
                <Select
                  value={form.warehouse_id || ""}
                  onValueChange={(v) => { setForm({ ...form, warehouse_id: v }); setFreeLocsOpen(false); }}
                >
                  <SelectTrigger className="rounded-sm" data-testid="st-warehouse">
                    <SelectValue placeholder="Depo seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses.map((w) => (
                      <SelectItem key={w.id} value={w.id}>
                        {w.name}{w.is_default ? " (varsayılan)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Raf / Konum</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="A-12" className="rounded-sm font-mono" data-testid="st-loc" />
                <button
                  type="button"
                  onClick={showFreeLocations}
                  disabled={freeLocsLoading}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600 hover:text-amber-700 disabled:opacity-50"
                  data-testid="st-free-loc-btn"
                >
                  {freeLocsLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <MapPin className="h-3 w-3" />}
                  {freeLocsOpen ? "Gizle" : "Boş yer göster"}
                </button>
                {freeLocsOpen && (
                  <div className="mt-1.5 rounded-sm border border-border bg-muted/40 p-2" data-testid="st-free-loc-list">
                    {freeLocs.length === 0 ? (
                      <p className="text-xs text-muted-foreground">Bu depoda boş (daha önce kullanılmış) yer yok.</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {freeLocs.map((loc) => (
                          <button
                            key={loc}
                            type="button"
                            onClick={() => { setForm((f) => ({ ...f, location: loc })); setFreeLocsOpen(false); }}
                            className="rounded-sm border border-border bg-card px-2 py-1 text-xs font-mono hover:border-amber-500 hover:text-amber-700"
                            data-testid={`st-free-loc-${loc}`}
                          >
                            {loc}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Lastikler — birden fazla marka/özellik/adet eklenebilir */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="label-eyebrow">Lastikler</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addTire}
                  className="rounded-sm"
                  data-testid="add-tire-btn"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> Lastik Ekle
                </Button>
              </div>

              {tires.map((t, idx) => (
                <div key={idx} className="rounded-sm border border-border p-3 space-y-3 relative" data-testid={`tire-line-${idx}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground">Lastik #{idx + 1}</span>
                    {tires.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeTire(idx)}
                        className="inline-flex items-center gap-1 text-xs text-destructive hover:underline"
                        data-testid={`remove-tire-${idx}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Kaldır
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="label-eyebrow">Marka *</Label>
                      <Input value={t.brand} onChange={(e) => setTire(idx, { brand: e.target.value })} placeholder="Bridgestone" className="rounded-sm" data-testid={`st-brand-${idx}`} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="label-eyebrow">Ebat *</Label>
                      <Input value={t.size} onChange={(e) => setTire(idx, { size: e.target.value })} placeholder="205/55 R16" className="rounded-sm font-mono" data-testid={`st-size-${idx}`} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="label-eyebrow">DOT</Label>
                      <Input value={t.dot} onChange={(e) => setTire(idx, { dot: e.target.value })} placeholder="2823" className="rounded-sm font-mono" data-testid={`st-dot-${idx}`} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="label-eyebrow">Diş Derinliği (mm)</Label>
                      <Input type="number" step="0.1" inputMode="decimal" value={t.tread_depth_mm} onChange={(e) => setTire(idx, { tread_depth_mm: e.target.value })} className="rounded-sm" data-testid={`st-tread-${idx}`} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="label-eyebrow">Adet *</Label>
                      <Input type="number" inputMode="numeric" min="1" value={t.quantity} onChange={(e) => setTire(idx, { quantity: e.target.value })} className="rounded-sm" data-testid={`st-qty-${idx}`} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="label-eyebrow">Sezon</Label>
                      <Select value={t.season} onValueChange={(v) => setTire(idx, { season: v })}>
                        <SelectTrigger className="rounded-sm" data-testid={`st-season-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="kis">Kış</SelectItem>
                          <SelectItem value="yaz">Yaz</SelectItem>
                          <SelectItem value="dort_mevsim">4 Mevsim</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Ortak: ücret (tek) + tahmini teslim + not */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Ücret (TL)</Label>
                <Input type="number" inputMode="decimal" value={form.fee} onChange={(e) => setForm({ ...form, fee: e.target.value })} className="rounded-sm" data-testid="st-fee" />
                <p className="text-[11px] text-muted-foreground">Girilen tüm lastikler için tek ücret.</p>
              </div>
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Tahmini Teslim Tarihi</Label>
                <Input type="date" value={form.expected_pickup_date} onChange={(e) => setForm({ ...form, expected_pickup_date: e.target.value })} className="rounded-sm" data-testid="st-pickup" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="label-eyebrow">Not</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm" data-testid="st-notes" />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">İptal</Button>
              <Button type="submit" disabled={saving} className="rounded-sm bg-foreground text-background hover:bg-foreground/90" data-testid="storage-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
